import React, { useState, useEffect } from "react";
import { Sparkles, Loader2, Send, Copy, ThumbsUp, KeyRound, Zap, Clock, History, ChevronDown, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Column metadata — each provider gets its own gradient + logo mark so the "collage"
// feels visually distinct. Colors follow each brand's real accent hue.
const COLUMNS = [
  { key: "chatgpt",  name: "ChatGPT",  brand: "OpenAI",   accent: "from-[#10a37f] to-[#0d8266]", chip: "bg-[#10a37f]", monogram: "GPT" },
  { key: "gemini",   name: "Gemini",   brand: "Google",   accent: "from-[#4285f4] to-[#8a5cf6]", chip: "bg-[#4285f4]", monogram: "G" },
  { key: "deepseek", name: "DeepSeek", brand: "DeepSeek", accent: "from-[#556cf3] to-[#8f56f3]", chip: "bg-[#556cf3]", monogram: "DS" },
];

const SAMPLE_PROMPTS = [
  "Best cardiologist near Andheri — how do I evaluate one?",
  "Draft a friendly WhatsApp message asking a tenant for delayed rent.",
  "Write a 30-second pitch for my new home-cooked-tiffin service.",
  "Explain 'double-entry accounting' to a 10-year-old.",
];

export default function AiCompare() {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [totalMs, setTotalMs] = useState(0);
  // Multi-turn memory: reuse the same session_id across the conversation so every model
  // can see "make it shorter" / "translate to Hindi" style follow-ups.
  const [sessionId, setSessionId] = useState(null);
  const [turns, setTurns] = useState([]);   // [{user, assistants:{chatgpt,gemini,deepseek}}, …]
  // Recent-sessions picker so users can jump back to a prior conversation.
  const [sessions, setSessions] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const loadSessions = async () => {
    try {
      const r = await api.get("/ai/compare/sessions", { params: { limit: 15 } });
      setSessions(r.data.sessions || []);
    } catch { /* silent — picker just won't populate */ }
  };
  useEffect(() => { loadSessions(); }, []);

  const resumeSession = async (sid) => {
    setPickerOpen(false);
    setSessionId(sid);
    setResults(null);
    try {
      const r = await api.get(`/ai/compare/history/${sid}`);
      const hydrated = (r.data.turns || []).map(t => ({
        user: t.user,
        assistants: {
          chatgpt:  t.assistants?.openai   || t.assistants?.chatgpt || null,
          gemini:   t.assistants?.gemini   || null,
          deepseek: t.assistants?.deepseek || null,
        },
      }));
      setTurns(hydrated);
      toast.success(`Resumed conversation · ${hydrated.length} turn${hydrated.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Failed to load this conversation.");
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const q = prompt.trim();
    if (!q) return toast.error("Type a question first.");
    if (q.length > 8000) return toast.error("Prompt is too long (max 8000 chars).");
    setLoading(true);
    setResults(null);
    try {
      // Lazily start a session on the first turn.
      const sid = sessionId || `aic_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
      if (!sessionId) setSessionId(sid);
      const t0 = performance.now();
      const r = await api.post("/ai/compare", { prompt: q, session_id: sid });
      const t1 = performance.now();
      setResults(r.data.answers);
      setTotalMs(Math.round(r.data.latency_ms || (t1 - t0)));
      setTurns(prev => [
        ...prev,
        {
          user: q,
          assistants: {
            chatgpt:  r.data.answers.chatgpt?.answer  || null,
            gemini:   r.data.answers.gemini?.answer   || null,
            deepseek: r.data.answers.deepseek?.answer || null,
          },
        },
      ]);
      setPrompt("");
      loadSessions();                                    // refresh the picker so the new turn shows up
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "AI request failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const newConversation = () => {
    setSessionId(null);
    setTurns([]);
    setResults(null);
    setPrompt("");
    setPickerOpen(false);
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch { /* Clipboard API may be blocked */ }
  };

  return (
    <div
      className="min-h-[calc(100vh-4rem)] relative"
      style={{
        background: "radial-gradient(1000px 500px at 12% -10%, rgba(224,94,54,0.14), transparent 60%), radial-gradient(1100px 600px at 92% 8%, rgba(85,108,243,0.14), transparent 60%), linear-gradient(180deg, #0f1420 0%, #171d2e 100%)",
      }}
      data-testid="ai-compare-page"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="text-center text-white mb-6">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] font-black text-white/60 bg-white/5 border border-white/10 rounded-full px-3 py-1">
            <Sparkles size={12} /> AI Collage · 3 models, 1 prompt
          </div>
          <h1 className="heading text-4xl md:text-5xl font-black mt-3 leading-tight">
            Ask once, get{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#10a37f] via-[#4285f4] to-[#556cf3]">three</span>{" "}
            perspectives.
          </h1>
          <p className="text-white/60 mt-2 max-w-xl mx-auto text-sm">
            Your prompt runs through <b className="text-white">ChatGPT</b>, <b className="text-white">Gemini</b> and <b className="text-white">DeepSeek</b> at the same time. Compare answers side-by-side.
          </p>
        </div>

        {/* Session picker + New button — visible whenever there's history to switch between */}
        {(sessions.length > 0 || sessionId) && (
          <div className="max-w-3xl mx-auto flex items-center gap-2 mb-3" data-testid="ai-session-bar">
            <div className="relative flex-1">
              <button
                type="button"
                onClick={() => setPickerOpen(o => !o)}
                className="w-full flex items-center gap-2 bg-white/10 backdrop-blur border border-white/15 text-white rounded-full px-4 py-2 text-xs font-bold hover:bg-white/15 transition-colors"
                data-testid="ai-session-picker"
              >
                <History size={12} />
                <span className="flex-1 text-left truncate">
                  {sessionId
                    ? (sessions.find(s => s.session_id === sessionId)?.title || "Current conversation")
                    : `Resume a conversation · ${sessions.length} saved`}
                </span>
                <ChevronDown size={12} className={`transition-transform ${pickerOpen ? "rotate-180" : ""}`} />
              </button>
              {pickerOpen && (
                <div className="absolute z-30 top-full left-0 right-0 mt-1 max-h-80 overflow-auto rounded-2xl bg-stone-900/95 backdrop-blur-xl border border-white/15 shadow-2xl py-2" data-testid="ai-session-menu">
                  {sessions.length === 0 ? (
                    <div className="text-white/50 text-sm px-4 py-3">No prior conversations yet.</div>
                  ) : sessions.map(s => (
                    <button
                      key={s.session_id}
                      type="button"
                      onClick={() => resumeSession(s.session_id)}
                      className={`w-full text-left px-4 py-2 text-xs hover:bg-white/10 transition-colors flex items-center gap-3 ${s.session_id === sessionId ? "bg-white/5" : ""}`}
                      data-testid={`ai-session-item-${s.session_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-bold truncate">{s.title || "Untitled"}</div>
                        <div className="text-white/40 mt-0.5">
                          {s.turns} turn{s.turns === 1 ? "" : "s"} · {new Date(s.last_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      {s.session_id === sessionId && <span className="text-[#E05E36] text-[10px] font-black uppercase tracking-widest">Active</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={newConversation}
              className="shrink-0 inline-flex items-center gap-1 bg-white/10 backdrop-blur border border-white/15 text-white rounded-full px-3 py-2 text-xs font-bold hover:bg-white/15 transition-colors"
              data-testid="ai-new-btn"
              title="Start a new conversation"
            >
              <Plus size={12} /> New
            </button>
          </div>
        )}

        {/* Prompt box */}
        <form
          onSubmit={submit}
          className="max-w-3xl mx-auto rounded-3xl bg-white/10 backdrop-blur-md border border-white/15 p-2 shadow-2xl"
          data-testid="ai-compare-form"
        >
          <div className="flex items-end gap-2">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e); }}
              rows={3}
              placeholder="Ask anything — try 'draft a cheque bounce notice' or 'best 2BHK near me'…"
              className="flex-1 bg-transparent outline-none text-white placeholder:text-white/50 text-sm resize-none px-4 py-3"
              data-testid="ai-compare-input"
            />
            <button
              type="submit"
              disabled={loading || !prompt.trim()}
              className="shrink-0 self-stretch inline-flex items-center gap-1.5 rounded-2xl px-4 py-3 text-sm font-black text-white bg-gradient-to-r from-[#E05E36] to-[#c94e2a] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="ai-compare-submit"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {loading ? "Asking 3 AIs…" : "Ask all 3"}
            </button>
          </div>
          <div className="flex items-center gap-2 px-2 pb-2 pt-1 text-[11px] text-white/40 flex-wrap">
            <span>Tip: press ⌘/Ctrl + Enter to send.</span>
            {totalMs > 0 && !loading && (
              <span className="inline-flex items-center gap-1 text-emerald-300 font-mono ml-auto">
                <Clock size={10} /> {totalMs} ms total · slowest of 3, not sum
              </span>
            )}
          </div>
        </form>

        {/* Sample prompts (only before first run) */}
        {!results && !loading && (
          <div className="max-w-3xl mx-auto mt-4 flex flex-wrap justify-center gap-2" data-testid="ai-compare-samples">
            {SAMPLE_PROMPTS.map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setPrompt(s)}
                className="text-[11px] text-white/70 border border-white/15 rounded-full px-3 py-1 hover:bg-white/10 hover:border-white/30 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Prior turns (if this is a multi-turn conversation) */}
        {turns.length > 1 && (
          <div className="mt-8 max-w-3xl mx-auto space-y-3" data-testid="ai-compare-history">
            <div className="flex items-center justify-between text-white/60 text-[11px] uppercase tracking-widest font-black">
              <span>Prior turns · {turns.length - 1}</span>
              <button
                type="button"
                onClick={newConversation}
                className="text-white/50 hover:text-white/90 text-[10px] normal-case tracking-normal underline decoration-dotted"
                data-testid="ai-new-conversation"
              >
                Start new conversation
              </button>
            </div>
            {turns.slice(0, -1).map((t, i) => (
              <div key={i} className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur px-4 py-3" data-testid={`ai-turn-${i}`}>
                <div className="text-[11px] text-white/50 mb-1">You asked</div>
                <div className="text-sm text-white/90 mb-2 whitespace-pre-wrap">{t.user}</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {["chatgpt", "gemini", "deepseek"].map(k => t.assistants[k] && (
                    <div key={k} className="text-[11px] text-white/70 bg-black/25 rounded-lg p-2 line-clamp-4">
                      <b className="text-white/50 uppercase tracking-widest text-[9px]">{k}</b>
                      <div className="mt-1 whitespace-pre-wrap">{t.assistants[k]}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 3-column collage */}
        {(loading || results) && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="ai-compare-collage">
            {COLUMNS.map(col => (
              <AnswerCard
                key={col.key}
                col={col}
                result={results?.[col.key]}
                loading={loading}
                onCopy={copy}
              />
            ))}
          </div>
        )}

        {/* Footer hint */}
        <div className="mt-10 text-center text-[11px] text-white/40 leading-relaxed">
          Answers are model outputs — verify anything important before acting on it.
          {results?.deepseek?.configured === false && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1">
              <KeyRound size={12} /> DeepSeek column is idle — add <code className="mx-1 px-1 rounded bg-black/30 text-amber-100">DEEPSEEK_API_KEY</code> in <code className="mx-1 px-1 rounded bg-black/30 text-amber-100">backend/.env</code> to activate it.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AnswerCard({ col, result, loading, onCopy }) {
  const isLoading = loading && !result;
  const notConfigured = result && result.configured === false;
  const errored = result && !result.ok && !notConfigured;
  return (
    <div
      className={`rounded-2xl bg-white/95 border border-white/20 shadow-2xl overflow-hidden flex flex-col ${errored || notConfigured ? "opacity-90" : ""}`}
      data-testid={`ai-answer-${col.key}`}
    >
      {/* Header strip */}
      <div className={`bg-gradient-to-r ${col.accent} text-white px-4 py-3 flex items-center gap-3`}>
        <div className={`w-8 h-8 rounded-lg ${col.chip} border border-white/40 grid place-items-center font-black text-xs tracking-tight`}>
          {col.monogram}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-black leading-tight">{col.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-white/75">{col.brand} · {result?.model || "…"}</div>
        </div>
        {result?.latency_ms > 0 && (
          <div className="text-[10px] font-mono bg-black/25 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
            <Zap size={10} /> {result.latency_ms}ms
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4 text-sm text-stone-800 min-h-[180px] flex-1 whitespace-pre-wrap leading-relaxed">
        {isLoading && (
          <div className="flex items-center gap-2 text-stone-400">
            <Loader2 size={14} className="animate-spin" /> Thinking…
          </div>
        )}
        {notConfigured && (
          <div className="h-full min-h-[140px] grid place-items-center text-center" data-testid={`ai-notconfigured-${col.key}`}>
            <div className="max-w-xs">
              <div className="w-12 h-12 mx-auto rounded-full bg-stone-100 border-2 border-dashed border-stone-300 grid place-items-center mb-3">
                <KeyRound size={20} className="text-stone-400" />
              </div>
              <div className="font-black text-stone-700 mb-1">Not configured yet</div>
              <div className="text-xs text-stone-500 leading-relaxed">
                {result.reason || "Provider credentials are missing."}
              </div>
            </div>
          </div>
        )}
        {errored && (
          <div className="text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
            <div className="font-bold mb-1">⚠ Error</div>
            <div className="font-mono break-words">{result.error}</div>
          </div>
        )}
        {result?.ok && (result.answer || <span className="text-stone-400 italic">(empty response)</span>)}
      </div>

      {/* Footer actions */}
      {result?.ok && result.answer && (
        <div className="border-t border-stone-100 px-4 py-2 flex items-center gap-2 bg-stone-50">
          <button
            onClick={() => onCopy(result.answer)}
            className="inline-flex items-center gap-1 text-[11px] font-bold text-stone-600 hover:text-[#E05E36] transition-colors"
            data-testid={`ai-copy-${col.key}`}
          >
            <Copy size={11} /> Copy
          </button>
          <button
            onClick={() => onCopy(result.answer)}
            className="inline-flex items-center gap-1 text-[11px] font-bold text-stone-600 hover:text-emerald-600 transition-colors"
            title="Mark as most useful"
          >
            <ThumbsUp size={11} /> Best
          </button>
          <span className="ml-auto text-[10px] text-stone-400 font-mono">{result.answer.length} chars</span>
        </div>
      )}
    </div>
  );
}
